import React, { useState } from 'react'
import styles from 'styles/components/AddPaymentMethodModal.module.sass'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { X as CloseIcon } from 'lucide-react';
import SecondaryButton from 'components/common/SecondaryButton';
import { useToast } from 'provider/ToastProvider';


interface AddPaymentMethodModalProps {
    isOpen: boolean;
    onClose: () => void;
    clientSecret: string;
    onPaymentMethodAdded: () => void;
}

/**
 * AddPaymentMethodModal component - A modal dialog for adding a new payment method using Stripe.
 * 
 * @component
 * @param {AddPaymentMethodModalProps} props The component props.
 * @param {boolean} props.isOpen Indicates whether the modal is open.
 * @param {() => void} props.onClose Function to call when the modal is closed.
 * @param {string} props.clientSecret The client secret from Stripe for confirming the card setup.
 * @param {() => void} props.onPaymentMethodAdded Callback function to call after a payment method is successfully added.
 * 
 * @returns {JSX.Element} AddPaymentMethodModal component
 * 
 * @example
 * <AddPaymentMethodModal
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   clientSecret="seti_1HxxxxxxSecretKey"
 *   onPaymentMethodAdded={() => console.log("Payment method added")}
 * />
 */
const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({ isOpen, onClose, clientSecret, onPaymentMethodAdded }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const { addToast } = useToast();

    const cardElementOptions = {
        style: {
        base: {
            width: '100%',
            height: 'auto',
            minHeight: 'auto',
            fontSize: '18px',
            color: '#424770',
            letterSpacing: '0.025em',
            fontFamily: 'Poppins, sans-serif',
            padding: '10px',
            '::placeholder': {
            color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
        },
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            if (!stripe || !elements) {
                return;
            }

            setIsLoading(true);

            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                setIsLoading(false);
                return;
            }

            const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (error || !setupIntent) {
                addToast({ type: 'error', message: error?.message || 'Failed to add payment method.' });
                setIsLoading(false);
                return;
            }

            onPaymentMethodAdded();
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent className={'sm:max-w-md'}>
                <div className={styles['dialog-header']}>
                    <p className={styles['dialog-title']}>Add Payment Method</p>
                    <IconButton 
                        onClick={onClose}
                        aria-label='close'
                        className={styles['close-button'] ?? 'close-button'}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
                <form
                    onSubmit={(event: React.FormEvent) => {
                        void handleSubmit(event);
                    }}
                    className={styles['payment-form']}
                >
                    <div className={styles['card-element-container']}>
                        <CardElement options={cardElementOptions} />
                    </div>
                    <SecondaryButton
                        text={isLoading ? 'Processing...' : 'Save Payment Method'}
                        width="100%"
                        primaryColor="#ff5522"
                        textColor="#ffffff"
                        hoverColor="#ff784d"
                        hoverTextColor="#ffffff"
                        icon={'material-symbols:credit-card-plus-outline'}
                        doSomething={() => {}}
                        disabled={!stripe || isLoading}
                    />
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddPaymentMethodModal;